import React, { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { Stack } from '@mui/material'
import Grid from '@mui/material/GridLegacy'

import { useStores } from '@trivie/core'
import { Insight } from '@trivie/ui-web/components/insight'
import { Panel } from '@trivie/ui-web/components/panel/panel'
import { Text } from '@trivie/ui-web/components/text'
import { color } from '@trivie/ui-web/theme'

import { SubscriptionPurchasesTable } from '../subscription-purchases-table'
import { SubscriptionTagsPanel } from '../subscription-tags-panel'

const COLOR_SWATCH: React.CSSProperties = {
  width: 16,
  height: 16,
  borderRadius: 4,
  border: '1px solid rgba(0,0,0,0.1)',
}

const Overview = observer(({ id }: { id: string }) => {
  const { subscriptionStore } = useStores()
  const { getPurchases, purchaseStats } = subscriptionStore

  const product = subscriptionStore.selectedProduct

  useEffect(() => {
    getPurchases(Number(id))
  }, [id])

  const { active, expired, failed, total } = purchaseStats

  if (!product) return null

  return (
    <Stack gap={2}>
      <Grid container spacing={2}>
        <Grid xs={12} item md={6} lg={3} sx={{ width: '100%' }}>
          <Panel fill sx={{ flex: 1 }}>
            <Insight
              title="Active"
              value={String(active)}
              textStyle={{ color: active ? color.green : '#888' }}
            />
          </Panel>
        </Grid>
        <Grid xs={12} item md={6} lg={3} sx={{ width: '100%' }}>
          <Panel fill sx={{ flex: 1 }}>
            <Insight title="Expired" value={String(expired)} textStyle={{ color: '#888' }} />
          </Panel>
        </Grid>
        <Grid item xs={12} md={6} lg={3} sx={{ width: '100%' }}>
          <Panel fill sx={{ flex: 1 }}>
            <Insight
              title="Failed"
              value={String(failed)}
              textStyle={{ color: failed === 0 ? '#888' : color.red }}
            />
          </Panel>
        </Grid>
        <Grid item xs={12} md={6} lg={3} sx={{ width: '100%' }}>
          <Panel fill sx={{ flex: 1 }}>
            <Insight title="Total" value={String(total)} textStyle={{ color: '#888' }} />
          </Panel>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item md={12} lg={6} sx={{ width: '100%' }}>
          <Panel title="Subscription Details" fill>
            <Stack gap={2} direction="column">
              <Stack gap={'83px'} direction="row">
                <Text
                  fontWeight="600"
                  variant="body1Medium"
                  text="SKU"
                  sx={{ color: 'text.secondary' }}
                />
                <Text variant="body1" text={product.sku} />
              </Stack>
              <Stack gap={'35px'} direction="row">
                <Text
                  fontWeight="600"
                  variant="body1Medium"
                  text="Description"
                  sx={{ color: 'text.secondary' }}
                />
                <Text variant="body1" text={product.description} />
              </Stack>
              {product.color && (
                <Stack gap={'77px'} direction="row">
                  <Text
                    fontWeight="600"
                    variant="body1Medium"
                    text="Color"
                    sx={{ color: 'text.secondary' }}
                  />
                  <Stack direction="row" alignItems="center" gap={1}>
                    <div style={{ ...COLOR_SWATCH, backgroundColor: product.color }} />
                    <Text variant="body1" text={product.color.toLocaleUpperCase()} />
                  </Stack>
                </Stack>
              )}
              {/* <Stack gap={'59px'} direction="row">
                <Text
                  fontWeight="600"
                  variant="body1Medium"
                  text="Created"
                  sx={{ color: 'text.secondary' }}
                />
                <Text variant="body1" text={product.createdDate} />
              </Stack> */}
              <Stack gap={'55px'} direction="row">
                <Text
                  fontWeight="600"
                  variant="body1Medium"
                  text="Duration"
                  sx={{ color: 'text.secondary' }}
                />
                <Text variant="body1" text={product.durationText} />
              </Stack>
            </Stack>
          </Panel>
        </Grid>
        <Grid item md={12} lg={6}>
          <SubscriptionTagsPanel tagIds={product.tag_ids ?? []} />
        </Grid>
      </Grid>
      <SubscriptionPurchasesTable variant="overview" productId={Number(id)} />
    </Stack>
  )
})

export const SubscriptionProductOverviewTab = (props: any) => {
  const { id } = props
  return {
    name: 'Overview',
    path: `/activities/subscriptions/${id}/overview`,
    component: () => <Overview id={id} />,
  }
}
